// src/sim/arena.ts — ArenaQuery uygulaması (ARCHITECTURE §4.2): groundY / edgeDist / isInside / blockedX / blockedZ.
// Yürünebilir (walkable) platformların BİRLEŞİMİ tek zemin kabul edilir: bitişik parçaların ortak kenarı gerçek kenar değildir
// (edgeDist ışın yürütmesiyle komşu parçaya geçer). Kenar toleranslı değil: ayak noktası AABB dışında = düşüş (DESIGN §8).
// three'siz. Sıcak yolda tahsis yok — iki önlem (determinism.test heap kabulü, ölçümle bulundu):
//   (1) platform sınırları Float64Array'de (nesne double alanı okuması yorumlayıcıda HeapNumber tahsis ediyordu);
//   (2) V8 etiketli çağrı sözleşmesinde inline edilmeyen fonksiyona geçen / dönen her Smi-olmayan double HeapNumber tahsis eder →
//       sıcak yol çağrıları double'ları `R` yazmaç dizisiyle geçirir (`*R` metotları); arayüz metotları (ArenaQuery) bunlara sarmalayıcıdır.
import type { ArenaDef, ArenaQuery, Vec3 } from '../core/types.ts';
import { CAP_H, GROUND_SNAP } from '../config/constants.ts';

/**
 * Sıcak yol "yazmaç" dizisi (sim geneli, tek iş parçacığı): fonksiyonlar arası double geçişi tahsissiz.
 * Yuva sözleşmesi: [0] x · [1] z · [2] y/yFrom/footY · [3] sonuç · [4] nx · [5] nz · [6] r · [7] seviye (topY)
 *                  [8] dx · [9] dz · [10] stickX · [11] stickZ · [12] hedef yaw · [13] dönüş hızı · [14] cx · [15] cz (iç yürüyüş)
 */
export const R = new Float64Array(16);

/** Kenar üstünde duran nokta için yön bakışında ufak içeri/dışarı kayma (float; sınır dahil kabul edilir). */
const EPS = 1e-6;
const SNAP = GROUND_SNAP;
const CAPH = CAP_H;

/** WP2 genişletmesi: R-yazmaçlı tahsissiz sorgular + platform indeksi + eksen boyunca kenar mesafesi. */
export interface ArenaQueryEx extends ArenaQuery {
  platformAt(x: number, z: number): number;
  /** (x,z) içerideyken eksen yönünde (dirX,dirZ ∈ {±1,0}) birleşim kenarına mesafe; dışarıda 0 (kenar freni eksen kırpması). */
  extentAlong(x: number, z: number, dirX: number, dirZ: number): number;
  /** R[0]=x R[1]=z R[2]=yFrom → R[3] = zemin y (NaN yok). */
  groundYR(): void;
  /** R[0]=x R[1]=z → R[3] = kenar mesafesi (+ içeride / − dışarıda), R[4],R[5] = dış normal. */
  edgeDistR(): void;
  /** R[0]=x R[1]=z → içeride mi. */
  isInsideR(): boolean;
  /** R[0]=x R[1]=z, dirX/dirZ ∈ {±1,0} (Smi) → R[3] = eksen boyunca kenara mesafe (dışarıda 0). */
  extentAlongR(dirX: number, dirZ: number): void;
  /** R[0]=x R[1]=z R[2]=footY R[6]=r → yan yüz kesişimi var mı (X/Z aynı test). */
  wallHitR(): boolean;
  /** R[0]=x R[1]=z → platform indeksi (arena.platforms) ya da −1. */
  platformAtR(): number;
}

class ArenaQueryImpl implements ArenaQueryEx {
  // yürünebilir platformlar (w*) ve tüm platformlar (a*)
  private readonly n: number;
  private readonly wMinX: Float64Array; private readonly wMaxX: Float64Array;
  private readonly wMinZ: Float64Array; private readonly wMaxZ: Float64Array;
  private readonly wTopY: Float64Array; private readonly wIdx: Int32Array;
  private readonly m: number;
  private readonly aMinX: Float64Array; private readonly aMaxX: Float64Array;
  private readonly aMinZ: Float64Array; private readonly aMaxZ: Float64Array;
  private readonly aTopY: Float64Array; private readonly aBotY: Float64Array;

  constructor(arena: ArenaDef) {
    const all = arena.platforms;
    this.m = all.length;
    this.aMinX = new Float64Array(this.m); this.aMaxX = new Float64Array(this.m);
    this.aMinZ = new Float64Array(this.m); this.aMaxZ = new Float64Array(this.m);
    this.aTopY = new Float64Array(this.m); this.aBotY = new Float64Array(this.m);
    let n = 0;
    for (let i = 0; i < all.length; i++) {
      const p = all[i]!;
      this.aMinX[i] = p.minX; this.aMaxX[i] = p.maxX; this.aMinZ[i] = p.minZ; this.aMaxZ[i] = p.maxZ;
      this.aTopY[i] = p.topY; this.aBotY[i] = p.topY - p.height;
      if (p.walkable) n++;
    }
    this.n = n;
    this.wMinX = new Float64Array(n); this.wMaxX = new Float64Array(n);
    this.wMinZ = new Float64Array(n); this.wMaxZ = new Float64Array(n);
    this.wTopY = new Float64Array(n); this.wIdx = new Int32Array(n);
    let k = 0;
    for (let i = 0; i < all.length; i++) {
      const p = all[i]!;
      if (!p.walkable) continue;
      this.wMinX[k] = p.minX; this.wMaxX[k] = p.maxX; this.wMinZ[k] = p.minZ; this.wMaxZ[k] = p.maxZ;
      this.wTopY[k] = p.topY; this.wIdx[k] = i; k++;
    }
  }

  /** R[0],R[1]'i içeren en yüksek yürünebilir platform (w indeksi); yoksa −1. Tam sayı döner (Smi, tahsissiz). */
  private findAnyR(): number {
    const x = R[0]!, z = R[1]!;
    let best = -1, bestY = -1e300;
    for (let i = 0; i < this.n; i++) {
      if (x >= this.wMinX[i]! && x <= this.wMaxX[i]! && z >= this.wMinZ[i]! && z <= this.wMaxZ[i]! && this.wTopY[i]! > bestY) {
        best = i; bestY = this.wTopY[i]!;
      }
    }
    return best;
  }
  /** R[14],R[15] noktasını içeren, seviyesi R[7] olan yürünebilir platform; yoksa −1. */
  private findAtR(): number {
    const x = R[14]!, z = R[15]!, topY = R[7]!;
    for (let i = 0; i < this.n; i++) {
      if (this.wTopY[i]! === topY && x >= this.wMinX[i]! && x <= this.wMaxX[i]! && z >= this.wMinZ[i]! && z <= this.wMaxZ[i]!) return i;
    }
    return -1;
  }
  /** R[0],R[1]'den (dirX,dirZ) eksen yönünde birleşim kenarına mesafe (seviye R[7]) → R[3]. Parçadan parçaya yürür. */
  private extentR(dirX: number, dirZ: number): void {
    let d = 0, cx = R[0]!, cz = R[1]!;
    for (let iter = 0; iter <= this.n; iter++) {
      R[14] = cx + dirX * EPS; R[15] = cz + dirZ * EPS;
      const i = this.findAtR();
      if (i < 0) break;
      const t = dirX > 0 ? this.wMaxX[i]! - cx : dirX < 0 ? cx - this.wMinX[i]! : dirZ > 0 ? this.wMaxZ[i]! - cz : cz - this.wMinZ[i]!;
      if (t <= 0) break;   // sınırda takılma koruması
      d += t; cx += dirX * t; cz += dirZ * t;
    }
    R[3] = d;
  }

  // ── R-yazmaçlı sıcak yol ──
  groundYR(): void {
    const x = R[0]!, z = R[1]!, lim = R[2]! + SNAP;
    let best = NaN;
    for (let i = 0; i < this.n; i++) {
      const ty = this.wTopY[i]!;
      if (ty <= lim && x >= this.wMinX[i]! && x <= this.wMaxX[i]! && z >= this.wMinZ[i]! && z <= this.wMaxZ[i]! && !(ty <= best)) best = ty;
    }
    R[3] = best;
  }
  edgeDistR(): void {
    const i = this.findAnyR();
    if (i >= 0) {
      R[7] = this.wTopY[i]!;
      this.extentR(1, 0); let best = R[3]!; let nx = 1, nz = 0;
      this.extentR(-1, 0); if (R[3]! < best) { best = R[3]!; nx = -1; nz = 0; }
      this.extentR(0, 1); if (R[3]! < best) { best = R[3]!; nx = 0; nz = 1; }
      this.extentR(0, -1); if (R[3]! < best) { best = R[3]!; nx = 0; nz = -1; }
      R[3] = best; R[4] = nx; R[5] = nz;
      return;
    }
    // dışarıda: en yakın yürünebilir AABB'ye işaretli (−) mesafe; normal = kenar noktasından dışarı
    const x = R[0]!, z = R[1]!;
    let bestD = 1e300, bx = 1, bz = 0;
    for (let k = 0; k < this.n; k++) {
      const cx = x < this.wMinX[k]! ? this.wMinX[k]! : x > this.wMaxX[k]! ? this.wMaxX[k]! : x;
      const cz = z < this.wMinZ[k]! ? this.wMinZ[k]! : z > this.wMaxZ[k]! ? this.wMaxZ[k]! : z;
      const dx = x - cx, dz = z - cz;
      const dd = Math.sqrt(dx * dx + dz * dz);
      if (dd < bestD) { bestD = dd; bx = dx; bz = dz; }
    }
    if (bestD >= 1e300) { R[3] = -Infinity; R[4] = 1; R[5] = 0; return; }
    const inv = bestD > 0 ? 1 / bestD : 0;
    R[3] = -bestD; R[4] = bestD > 0 ? bx * inv : 1; R[5] = bz * inv;
  }
  isInsideR(): boolean { return this.findAnyR() >= 0; }
  extentAlongR(dirX: number, dirZ: number): void {
    const i = this.findAnyR();
    if (i < 0) { R[3] = 0; return; }
    R[7] = this.wTopY[i]!;
    this.extentR(dirX, dirZ);
  }
  /** Kapsül dairesi (R[6]) ile ayaktan (R[2]) yüksek bir platform yan yüzü kesişiyor mu (X/Z aynı test; eksen-eksen çağrılır). */
  wallHitR(): boolean {
    const x = R[0]!, z = R[1]!, footY = R[2]!, r = R[6]!;
    for (let i = 0; i < this.m; i++) {
      if (this.aTopY[i]! <= footY + SNAP) continue;       // ayak seviyesinde/altında: zemin, duvar değil
      if (this.aBotY[i]! >= footY + CAPH) continue;       // kapsülün üstünde
      const cx = x < this.aMinX[i]! ? this.aMinX[i]! : x > this.aMaxX[i]! ? this.aMaxX[i]! : x;
      const cz = z < this.aMinZ[i]! ? this.aMinZ[i]! : z > this.aMaxZ[i]! ? this.aMaxZ[i]! : z;
      const dx = x - cx, dz = z - cz;
      if (dx * dx + dz * dz < r * r) return true;
    }
    return false;
  }
  platformAtR(): number { const i = this.findAnyR(); return i < 0 ? -1 : this.wIdx[i]!; }

  // ── ArenaQuery arayüzü (bot/HUD/diğer paketler; double argümanlı — soğuk yol) ──
  groundY(x: number, z: number, yFrom: number): number { R[0] = x; R[1] = z; R[2] = yFrom; this.groundYR(); return R[3]!; }
  edgeDist(x: number, z: number, outN: Vec3): number { R[0] = x; R[1] = z; this.edgeDistR(); outN.x = R[4]!; outN.y = 0; outN.z = R[5]!; return R[3]!; }
  isInside(x: number, z: number): boolean { R[0] = x; R[1] = z; return this.isInsideR(); }
  blockedX(x: number, z: number, footY: number, r: number): boolean { R[0] = x; R[1] = z; R[2] = footY; R[6] = r; return this.wallHitR(); }
  blockedZ(x: number, z: number, footY: number, r: number): boolean { R[0] = x; R[1] = z; R[2] = footY; R[6] = r; return this.wallHitR(); }
  platformAt(x: number, z: number): number { R[0] = x; R[1] = z; return this.platformAtR(); }
  extentAlong(x: number, z: number, dirX: number, dirZ: number): number { R[0] = x; R[1] = z; this.extentAlongR(dirX, dirZ); return R[3]!; }
}

export function createArenaQuery(arena: ArenaDef): ArenaQueryEx { return new ArenaQueryImpl(arena); }

/** Verilen sorgu WP2'nin R-yazmaçlı uygulaması mı (yabancı ArenaQuery → arayüz yolu). */
export const isQueryEx = (q: ArenaQuery): q is ArenaQueryEx => typeof (q as ArenaQueryEx).edgeDistR === 'function';

/** Yardımcı: outN scratch'i olmadan yalnız mesafe (bot/HUD; tahsissiz — modül scratch'i). */
const N_SCRATCH: Vec3 = { x: 0, y: 0, z: 0 };
export const edgeDistOnly = (q: ArenaQuery, x: number, z: number): number => q.edgeDist(x, z, N_SCRATCH);
